import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { KeyRound, ShieldCheck, Wallet } from 'lucide-react';
import { api, ApiError } from '../api';
import type { WhoamiResponse } from '../api';
import { Button, Card, ErrorBanner, Spinner, TextInput } from '../ui/primitives';
import { useWhoami } from '../lib/auth';

export function LoginPage() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const whoami = useWhoami();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);

  const loginMut = useMutation({
    mutationFn: async () => {
      await api.post<WhoamiResponse>('/api/auth/login', { username, password });
    },
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: ['whoami'] });
      nav('/', { replace: true });
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : String(err)),
  });

  const bootstrapMut = useMutation({
    mutationFn: async () => {
      if (password !== confirm) throw new Error('Passwords do not match.');
      await api.post<WhoamiResponse>('/api/auth/bootstrap', {
        username,
        password,
        pin: pin.trim() || null,
      });
    },
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: ['whoami'] });
      nav('/', { replace: true });
    },
    onError: (err) => setError(err instanceof Error ? err.message : String(err)),
  });

  if (whoami.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg text-ink-muted">
        <div className="flex items-center gap-2">
          <Spinner /> loading...
        </div>
      </div>
    );
  }

  const isBootstrap = whoami.data?.state === 'bootstrap_required';

  return (
    <div className="flex min-h-dvh items-center justify-center bg-bg px-4 py-6 font-ui text-ink sm:py-10">
      <div className="w-full max-w-sm space-y-4 native-screen-enter">
        <div className="rounded-[30px] border border-border/70 bg-[linear-gradient(145deg,rgba(255,255,255,0.96),rgba(223,243,238,0.92))] px-4 py-5 shadow-card backdrop-blur-xl sm:px-5">
          <div className="inline-flex items-center gap-2 rounded-full bg-[rgba(16,26,45,0.94)] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-white shadow-sm">
            <span className="h-1.5 w-1.5 rounded-full bg-[var(--brand)]" aria-hidden />
            SRB Accounts
          </div>
          <div className="mt-4 flex items-start justify-between gap-3">
            <div>
              <h1 className="text-[1.45rem] font-semibold tracking-[-0.03em] text-ink">
                {isBootstrap ? 'Create the owner account' : 'Sign in'}
              </h1>
              <p className="mt-1.5 text-[13px] leading-5 text-ink-muted">
                {isBootstrap
                  ? 'Finish the first setup on one screen and keep the security controls close.'
                  : 'Private access only. Your username and password were created by the owner.'}
              </p>
            </div>
            <span
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[16px] bg-white text-brand shadow-sm ring-1 ring-border/70"
              aria-hidden
            >
              <Wallet className="h-[22px] w-[22px]" strokeWidth={2} />
            </span>
          </div>
          <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-surface px-3 py-1.5 text-[11px] font-medium text-ink-muted ring-1 ring-border/60">
            <ShieldCheck className="h-3.5 w-3.5 text-brand" strokeWidth={2} />
            PIN-protected actions stay locked behind the session
          </div>
        </div>

        <Card className="p-4 sm:p-5">
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              setError(null);
              if (isBootstrap) bootstrapMut.mutate();
              else loginMut.mutate();
            }}
          >
            <TextInput
              label="Username"
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              name="username"
            />
            <TextInput
              label={isBootstrap ? 'New password (10+ chars)' : 'Password'}
              type="password"
              autoComplete={isBootstrap ? 'new-password' : 'current-password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              name="password"
            />
            {isBootstrap && (
              <>
                <TextInput
                  label="Confirm password"
                  type="password"
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  name="confirm"
                />
                <TextInput
                  label="PIN (optional)"
                  hint="Used for destructive actions. You can change it later."
                  type="password"
                  autoComplete="new-password"
                  value={pin}
                  onChange={(e) => setPin(e.target.value)}
                  name="pin"
                />
              </>
            )}
            {error && <ErrorBanner>{error}</ErrorBanner>}
            <Button
              type="submit"
              className="w-full"
              disabled={loginMut.isPending || bootstrapMut.isPending}
            >
              {loginMut.isPending || bootstrapMut.isPending ? (
                <Spinner />
              ) : (
                <span className="inline-flex items-center gap-2">
                  <KeyRound className="h-4 w-4" strokeWidth={2} />
                  {isBootstrap ? 'Create owner' : 'Sign in'}
                </span>
              )}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}
